import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useIsScreenSize } from '@app/utils/hooks/useIsScreenSize';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { IconComponent, Notifications, Toasts } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Link, To } from 'react-router-dom';

import { Button } from '../Button';
import { NotificationWrapper } from '../Notification/NotificationWrapper';

import { dismissToast } from './toast';
import styles from './Toaster.module.scss';

interface Props {
  id: string;
  message: React.ReactNode;
  type?: Notifications.NotificationType;
  description?: React.ReactNode;
  icon?: IconComponent;
  actions?: Toasts.Actions;
  visible: boolean;
  onClose?: () => void;
  dataTestId?: string;
}

export const ToastBar = React.memo(
  ({ actions = [], id, type = 'info', message, dataTestId, description, visible, icon, onClose }: Props) => {
    const dispatch = useDispatch();
    const isMobile = useIsScreenSize(BREAKPOINTS.SM);

    const handleClose = () => {
      dismissToast(id);
      onClose?.();
    };

    const handleActionClick = (action: Toasts.Action) => {
      action.onClick?.();
      handleClose();

      if (isDefined(action.action)) {
        dispatch(action.action);
      }
    };

    return (
      <NotificationWrapper
        type={type}
        role="alert"
        icon={icon}
        className={cx('pointer-events-auto shadow-2 will-change-transform sm:max-w-lg', {
          [styles.enter]: visible,
          [styles.exit]: !visible,
        })}
        isClosable
        isFluid={isMobile}
        dataTestId={dataTestId}
        onClose={handleClose}
      >
        <div className="break-words body-1">{message}</div>
        {isDefined(description) && <div className="whitespace-pre-wrap text-neutral-40 body-5">{description}</div>}
        {!isEmpty(actions) && (
          <div className="mt-3 flex flex-wrap gap-3">
            {actions.map((action, index) => (
              <Button
                to={(isDefined(action.to) ? action.to : undefined) as To}
                as={isDefined(action.to) ? Link : undefined}
                key={action.label}
                variant={index === 0 ? 'flat' : 'ghost'}
                color="secondary"
                size="medium"
                onClick={() => handleActionClick(action)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </NotificationWrapper>
    );
  },
);
